/*
 * A copy of the dispatch console's component of the same name.
 *
 * The six states the design commission asks to be centralised, and the argument for centralising
 * them holds across consoles as well as across screens: an administrator working two consoles must
 * not learn two dialects of "nothing here".
 *
 * A copy rather than a shared package: see `docs/adr/0021-two-consoles-and-no-shared-shell.md`.
 */
import type { CSSProperties, ReactNode } from "react";

import { retryLabel, type ViewState } from "../view-state.ts";

/**
 * What a board hands over so the loading state can draw the design's skeleton rather than a word.
 *
 * Optional at every call site on purpose. A board that passes nothing still gets the word
 * `Loading`, so adopting this is one screen at a time against a measured number rather than
 * seventy-two screens at once against none.
 */
export interface LoadingSkeleton {
  /**
   * The board's own column headings, drawn above the shimmer.
   *
   * Handed over as an element rather than as labels because the ready state already draws them and
   * the design draws the *same* heading row in both states. Rebuilding it here from a list would be
   * a second copy of the board's grid that could disagree with the first.
   */
  readonly head: ReactNode;
  /** How many columns a row has; sets each bar's width, height and alignment. */
  readonly columns: number;
  /** The class the board's rows carry, so the shimmer stands on the board's own grid. */
  readonly rowClassName: string;
  /** The page's own name, as the breadcrumb says it. */
  readonly page: string;
  /** The tenant whose board is loading. */
  readonly tenant: string;
  /**
   * The class a board wraps its grid in, when it has one.
   *
   * `SurgeZones` draws its head and rows inside `records surge__grid`, which is what puts them on
   * a surface. Without it the skeleton would shimmer on the page background while every other
   * board shimmers on a card. Omitted by the boards whose rows carry their own background.
   */
  readonly wrapperClassName?: string;
}

/**
 * Six, which is what `02 Console Shell v3` draws: `[0,1,2,3,4,5].map(...)`.
 *
 * Not "enough to fill the viewport". The design draws six at every width, and a count that
 * responded to the viewport would be a different number from the design's at three of the four
 * widths the rules name.
 */
const SKELETON_ROWS = 6;

/** The design's own base widths, by column: `[70,60,55,80,65,40][j%6]`. */
const SKELETON_WIDTHS = [70, 60, 55, 80, 65, 40] as const;

/**
 * One shimmer bar, with the design's values rather than values that look like them.
 *
 * Transcribed from `02 Console Shell v3.dc.html`'s `skeleton`:
 *
 *     cells: colDefs.map((_, j) => ({ display:'block', height: j===0?22:10, borderRadius:999,
 *       background:'var(--muted)',
 *       width: j===colDefs.length-1 ? 16 : `${[70,60,55,80,65,40][j%6]-(i*7+j*3)%20}%`,
 *       justifySelf: j===colDefs.length-2 ? 'end' : 'start' }))
 *
 * The widths are deliberately uneven and deliberately not random: `(i*7+j*3)%20` subtracts up to
 * 19 points, so every row differs from its neighbour and the same board shimmers the same way
 * twice. A random width would make the comparison against the design unrepeatable, which is the
 * one thing a fidelity capture cannot be.
 *
 * `--muted` is the muted *fill*, not the muted text. The two carried the same name at different
 * times, and the consoles' `styles.css` records what happened the last time they were confused:
 * an element drew its text in its own background colour.
 */
function skeletonCell(row: number, column: number, columns: number): CSSProperties {
  return {
    display: "block",
    height: column === 0 ? 22 : 10,
    borderRadius: 999,
    background: "var(--muted)",
    width:
      column === columns - 1
        ? 16
        : `${String((SKELETON_WIDTHS[column % SKELETON_WIDTHS.length] ?? 0) - ((row * 7 + column * 3) % 20))}%`,
    justifySelf: column === columns - 2 ? "end" : "start",
  };
}

/**
 * The heading row, six shimmering rows and the loading sentence — on the board's own surface.
 *
 * All three inside `wrapperClassName` when a board has one, because in the design they are three
 * parts of one card: the note carries a `border-top` that has to meet the rows above it.
 */
function SkeletonGrid({ skeleton }: { readonly skeleton: LoadingSkeleton }): ReactNode {
  const inner = (
    <>
      {skeleton.head}
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <div key={row} className={skeleton.rowClassName}>
          {Array.from({ length: skeleton.columns }, (_, column) => (
            <span key={column} style={skeletonCell(row, column, skeleton.columns)} />
          ))}
        </div>
      ))}
      <p className="state__loading-note" role="status">
        {`Loading ${skeleton.page} for ${skeleton.tenant}. After 10 seconds this line says the request is still running; nothing is lost by waiting.`}
      </p>
    </>
  );

  return skeleton.wrapperClassName === undefined ? (
    inner
  ) : (
    <div className={skeleton.wrapperClassName}>{inner}</div>
  );
}

/**
 * The six states, rendered once.
 *
 * The design commission asks for them centralised, and the reason is not
 * consistency for its own sake: a screen that writes its own empty state writes
 * a slightly different one, and an operator working nine pages for a whole
 * shift learns nine dialects of "nothing here". Worse, the states that get
 * hand-written are always the easy ones — loading and empty — and the two that
 * matter most on this product, stale and conflict, are the two a hurried screen
 * silently omits.
 *
 * So a page hands its state here and gets the product's answer, including the
 * ones it did not think about.
 */

export interface StateViewProps {
  readonly state: ViewState;
  readonly purpose: string;
  readonly onRetry?: () => void;
  /** Rendered only in the ready states; a page never draws its own empty view. */
  readonly children?: ReactNode;
  /**
   * Given, the loading state draws the design's skeleton grid. Omitted, it draws the word.
   *
   * See Q-287 in `docs/design/DESIGN_QUESTIONS.md`: the word was a decision taken against the
   * design in this component's own comment, and it is now taken the other way.
   */
  readonly skeleton?: LoadingSkeleton;
}

export function StateView({
  state,
  purpose,
  onRetry,
  children,
  skeleton,
}: StateViewProps): ReactNode {
  const retry = retryLabel(state);
  const action =
    retry !== undefined && onRetry !== undefined ? (
      <button type="button" className="state__action" onClick={onRetry}>
        {retry}
      </button>
    ) : null;

  switch (state.kind) {
    case "loading":
      // The word, for the boards that have not been given a skeleton yet. This was the only
      // branch, and its comment — "a console reloads constantly and an operator does not need a
      // sentence each time" — was a decision against a designed state, taken in a component
      // rather than in the design file. Q-287, decided the other way on 31 October.
      if (skeleton === undefined)
        return (
          <div className="state state--loading" role="status" aria-live="polite">
            Loading
          </div>
        );

      /*
       * The design's skeleton: the heading row, six shimmering rows on the board's own grid, and a
       * sentence that names the page and the tenant and says waiting is safe.
       *
       * `aria-busy` is the design's own attribute on this container. `role="status"` stays on the
       * sentence rather than the container: the sentence is what a screen reader should read, and
       * announcing six rows of empty spans would be worse than announcing nothing. The fidelity
       * check matches landmarks by text content rather than by role, so neither attribute moves a
       * measurement either way.
       *
       * No card. `.state` draws 24px of padding and a border, which is right for the five states
       * that *are* a card and wrong for this one — the design draws the skeleton flush in the grid
       * where the rows will be, so the chrome above and below it does not move when the data
       * arrives. That is the whole point of a skeleton and it is why this branch does not carry the
       * `state` class.
       */
      return (
        <div className="state--loading state--skeleton" aria-busy="true">
          <SkeletonGrid skeleton={skeleton} />
        </div>
      );

    case "empty":
      return (
        <div className="state state--empty">
          <p className="state__headline">
            {state.because === "filtered" ? "Nothing matches this filter" : "Nothing here yet"}
          </p>
          <p className="state__detail">
            {state.because === "filtered"
              ? "Clear the filter to see everything on this page."
              : purpose}
          </p>
        </div>
      );

    case "forbidden":
      // Says what is missing rather than "access denied". An operator who is
      // told the scope can ask for it by name; one who is told "denied" opens a
      // ticket that someone else has to translate.
      return (
        <div className="state state--forbidden">
          <p className="state__headline">You do not have access to this</p>
          <p className="state__detail">
            {state.missingScopes.length > 0
              ? `It needs ${state.missingScopes.join(" or ")}. Ask whoever administers your roles for that permission.`
              : "Ask whoever administers your roles for access to this page."}
          </p>
        </div>
      );

    case "conflict":
      // Not styled as a failure, because it is not one. Somebody else got there
      // first and the operator needs to see what changed, not an apology.
      return (
        <div className="state state--conflict" role="alert">
          <p className="state__headline">Somebody changed this first</p>
          <p className="state__detail">{state.message}</p>
          <p className="state__code">{state.code}</p>
          {action}
        </div>
      );

    case "error":
      return (
        <div className="state state--error" role="alert">
          <p className="state__headline">
            {state.offline ? "The console cannot reach the platform" : "That did not work"}
          </p>
          <p className="state__detail">{state.message}</p>
          <p className="state__code">{state.code}</p>
          {action}
        </div>
      );

    case "ready":
      return (
        <>
          {state.stale ? (
            // Shown above the data rather than instead of it. The data is still
            // the best available; what an operator must not do is trust it as
            // current, and a driver marker that is thirty seconds old has
            // already moved.
            <div className="state__stale" role="status">
              <span>Last updated {Math.round(state.ageMs / 1000)}s ago — this may have moved</span>
              {action}
            </div>
          ) : null}
          {children}
        </>
      );
  }
}
